import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Building2, MapPin, Phone, Mail, Globe, Loader2, Save,
  Briefcase, Home, UtensilsCrossed, Heart, GraduationCap, Church,
  Palette, DollarSign, Scale, Building, Baby, Monitor, Car, Scissors,
  MoreHorizontal, Clock, Plus, Trash2, Tag, Percent, LocateFixed
} from "lucide-react";
import { 
  getBusinessListing, updateBusinessListing, getBusinessCategories, resolveImageUrl,
  geocodeAddress
} from "@/lib/api";
import { toast } from "sonner";
import MediaUploader from "@/components/MediaUploader";
import RichTextEditor, { isContentEmpty } from "@/components/RichTextEditor";

// Category icons mapping
const CATEGORY_ICONS = {
  "Professional Services": Briefcase,
  "Home Services": Home,
  "Food & Dining": UtensilsCrossed,
  "Health & Wellness": Heart,
  "Education": GraduationCap,
  "Religious Goods": Church,
  "Arts & Crafts": Palette,
  "Financial Services": DollarSign,
  "Legal Services": Scale,
  "Real Estate": Building,
  "Childcare": Baby,
  "Technology": Monitor,
  "Automotive": Car,
  "Beauty & Personal Care": Scissors,
  "Other": MoreHorizontal
};

const DAYS_OF_WEEK = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

export default function BusinessEditPage({ currentUser }) {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    about_business: "",
    service_area: "",
    service_radius_miles: 25,
    address: "",
    phone: "",
    email: "",
    website: "",
    about_owner: "",
    services_offered: [],
    hours: {
      monday: "",
      tuesday: "",
      wednesday: "",
      thursday: "",
      friday: "",
      saturday: "",
      sunday: "",
    },
    parishioner_discounts: [],
  });

  const [newService, setNewService] = useState("");
  const [newDiscount, setNewDiscount] = useState({
    title: "",
    description: "",
    discount_type: "percentage",
    discount_value: "",
    conditions: "",
  });
  const [geocoding, setGeocoding] = useState(false);
  const [coordinates, setCoordinates] = useState(null);

  useEffect(() => {
    loadCategories();
    loadListing();
  }, [listingId]);

  const loadCategories = async () => {
    try {
      const res = await getBusinessCategories();
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  const loadListing = async () => {
    setLoading(true);
    try {
      const res = await getBusinessListing(listingId);
      const listing = res.data;
      
      // Check ownership
      if (listing.owner_id !== currentUser?.user_id) {
        toast.error("You can only edit your own listings");
        setRedirecting(true);
        navigate("/directory");
        return;
      }
      
      setFormData({
        name: listing.name || "",
        category: listing.category || "",
        description: listing.description || "",
        about_business: listing.about_business || "",
        service_area: listing.service_area || "",
        service_radius_miles: listing.service_radius_miles || 25,
        address: listing.address || "",
        phone: listing.phone || "",
        email: listing.email || "",
        website: listing.website || "",
        about_owner: listing.about_owner || "",
        services_offered: listing.services_offered || [],
        hours: listing.hours || {
          monday: "",
          tuesday: "",
          wednesday: "",
          thursday: "",
          friday: "",
          saturday: "",
          sunday: "",
        },
        parishioner_discounts: listing.parishioner_discounts || [],
      });
      
      // Set photos
      if (listing.photos) {
        setPhotos(listing.photos.map((url, idx) => ({ 
          id: `photo-${idx}`, 
          url: url.startsWith('http') ? url : url 
        })));
      }
      
      // Set videos
      if (listing.videos) {
        setVideos(listing.videos.map((v, idx) => ({
          id: `video-${idx}`,
          url: typeof v === 'string' ? v : v.url,
          thumbnail: typeof v === 'object' ? v.thumbnail : null,
          name: typeof v === 'object' ? v.name : null,
          duration: typeof v === 'object' ? v.duration : null,
          duration_formatted: typeof v === 'object' ? v.duration_formatted : null,
        })));
      }
      
      // Set coordinates if they exist
      if (listing.coordinates) {
        setCoordinates(listing.coordinates);
      }
    } catch (err) {
      toast.error("Failed to load listing");
      setRedirecting(true);
      navigate("/directory");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error("Business name is required"); return; }
    if (!formData.category) { toast.error("Please select a category"); return; }
    if (isContentEmpty(formData.description)) { toast.error("Description is required"); return; }
    if (!formData.service_area.trim()) { toast.error("Service area is required"); return; }
    if (!formData.phone && !formData.email && !formData.website) {
      toast.error("At least one contact method is required"); return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        photos: photos.map(p => p.url),
        videos: videos.map(v => ({
          url: v.url,
          thumbnail: v.thumbnail || null,
          name: v.name || null,
          duration: v.duration || null,
          duration_formatted: v.duration_formatted || null,
        })),
      };
      
      // Include coordinates if geocoded
      if (coordinates) {
        submitData.coordinates = coordinates;
      }
      
      await updateBusinessListing(listingId, submitData);
      toast.success("Business listing updated!");
      navigate(`/directory/${listingId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update listing");
    }
    setSaving(false);
  };

  const handleGeocodeAddress = async () => {
    if (!formData.address || formData.address.trim().length < 5) {
      toast.error("Please enter a valid address to geocode");
      return;
    }
    
    setGeocoding(true);
    try {
      const res = await geocodeAddress(formData.address);
      if (res.data && res.data.lat && res.data.lng) {
        setCoordinates({ lat: res.data.lat, lng: res.data.lng });
        toast.success(`Location found: ${res.data.display_name || formData.address}`);
      } else {
        toast.error("Could not find that address. Try being more specific.");
      }
    } catch (err) {
      toast.error("Failed to geocode address");
    }
    setGeocoding(false);
  };

  const addService = () => {
    if (newService.trim() && !formData.services_offered.includes(newService.trim())) {
      setFormData({
        ...formData,
        services_offered: [...formData.services_offered, newService.trim()]
      });
      setNewService("");
    }
  };

  const removeService = (service) => {
    setFormData({
      ...formData,
      services_offered: formData.services_offered.filter(s => s !== service)
    });
  };

  const addDiscount = () => {
    if (newDiscount.title.trim() && newDiscount.description.trim()) {
      setFormData({
        ...formData,
        parishioner_discounts: [...formData.parishioner_discounts, { ...newDiscount }]
      });
      setNewDiscount({
        title: "",
        description: "",
        discount_type: "percentage",
        discount_value: "",
        conditions: "",
      });
    } else {
      toast.error("Discount title and description are required");
    }
  };

  const removeDiscount = (index) => {
    setFormData({
      ...formData,
      parishioner_discounts: formData.parishioner_discounts.filter((_, i) => i !== index)
    });
  };

  const updateHours = (day, value) => {
    setFormData({
      ...formData,
      hours: { ...formData.hours, [day]: value }
    });
  };

  if (loading || redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="business-edit-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/directory/${listingId}`)}
          className="mb-6 text-[#6B665E] hover:text-[#2D2A26] font-['Nunito']"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Listing
        </Button>

        <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26] mb-6">
          Edit Business Listing
        </h1>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Business Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Garcia Family Law"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Category *</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {categories.map(cat => {
                      const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                      return (
                        <SelectItem key={cat} value={cat} className="font-['Nunito'] text-sm">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-[#6B665E]" />
                            {cat}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Description *</Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => setFormData({...formData, description: value})}
                  placeholder="Describe your business and services..."
                  minHeight="120px"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">About Your Business</Label>
                <RichTextEditor
                  value={formData.about_business}
                  onChange={(value) => setFormData({...formData, about_business: value})}
                  placeholder="Tell customers what makes your business unique..."
                  minHeight="100px"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">About the Owner</Label>
                <RichTextEditor
                  value={formData.about_owner}
                  onChange={(value) => setFormData({...formData, about_owner: value})}
                  placeholder="Share a bit about yourself and your faith journey..."
                  minHeight="80px"
                  minimal={true}
                />
              </div>
            </CardContent>
          </Card>

          {/* Media */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
                Photos & Videos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MediaUploader
                photos={photos}
                videos={videos}
                onPhotosChange={setPhotos}
                onVideosChange={setVideos}
                maxPhotos={10}
                maxVideos={3}
                enableCropping={true}
              />
            </CardContent>
          </Card>

          {/* Location & Contact */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[var(--liturgical-primary)]" />
                Location & Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Service Area *</Label>
                  <Input 
                    value={formData.service_area}
                    onChange={e => setFormData({...formData, service_area: e.target.value})}
                    placeholder="e.g., Austin, TX or Nationwide"
                    className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Service Radius (miles)</Label>
                  <Input 
                    type="number"
                    value={formData.service_radius_miles}
                    onChange={e => setFormData({...formData, service_radius_miles: parseInt(e.target.value) || 25})}
                    className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Physical Address</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.address}
                    onChange={e => {
                      setFormData({...formData, address: e.target.value});
                      setCoordinates(null); // Clear coordinates when address changes
                    }}
                    placeholder="Full business address (for map display)"
                    className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGeocodeAddress}
                    disabled={geocoding || !formData.address}
                    className="border-[#E5E0D5] font-['Nunito']"
                  >
                    {geocoding ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <LocateFixed className="w-4 h-4" />
                    )}
                    <span className="ml-2 hidden md:inline">Find on Map</span>
                  </Button>
                </div>
                {coordinates && (
                  <p className="text-xs text-green-600 font-['Nunito'] flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    Location set: {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}
                  </p>
                )}
              </div>

              <Separator className="bg-[#E5E0D5]" />

              <p className="text-xs text-[#6B665E] font-['Nunito']">Contact (at least one required)</p>
              
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#6B665E]" />
                  <Input 
                    value={formData.phone} 
                    onChange={e => setFormData({...formData, phone: e.target.value})} 
                    placeholder="Phone number" 
                    className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" 
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#6B665E]" />
                  <Input 
                    value={formData.email} 
                    onChange={e => setFormData({...formData, email: e.target.value})} 
                    placeholder="Email address" 
                    className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" 
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#6B665E]" />
                  <Input 
                    value={formData.website} 
                    onChange={e => setFormData({...formData, website: e.target.value})} 
                    placeholder="Website URL" 
                    className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services Offered */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <Tag className="w-5 h-5 text-[var(--liturgical-primary)]" />
                Services Offered
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input 
                  value={newService}
                  onChange={e => setNewService(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addService())}
                  placeholder="e.g., Estate Planning, Tax Preparation"
                  className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1"
                />
                <Button 
                  type="button"
                  onClick={addService}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {formData.services_offered.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.services_offered.map((service, idx) => (
                    <Badge 
                      key={idx} 
                      className="bg-[#F9F7F2] text-[#2D2A26] font-['Nunito'] text-xs pr-1"
                    >
                      {service}
                      <button 
                        onClick={() => removeService(service)}
                        className="ml-2 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Business Hours */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[var(--liturgical-primary)]" />
                Business Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DAYS_OF_WEEK.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-4">
                    <span className="w-24 text-sm text-[#2D2A26] font-['Nunito']">{label}</span>
                    <Input 
                      value={formData.hours[key] || ""}
                      onChange={e => updateHours(key, e.target.value)}
                      placeholder="e.g., 9:00 AM - 5:00 PM or Closed"
                      className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-3">
                Leave blank if hours vary. Use "Closed" for days you're not open.
              </p>
            </CardContent>
          </Card>

          {/* Parishioner Discounts */}
          <Card className="bg-white border-[#E5E0D5]">
            <CardHeader>
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <Percent className="w-5 h-5 text-[var(--liturgical-primary)]" />
                Parishioner Discounts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-[#6B665E] font-['Nunito']">
                Offer special discounts to fellow parish members to build community support.
              </p>
              
              {/* Existing Discounts */}
              {formData.parishioner_discounts.length > 0 && (
                <div className="space-y-3">
                  {formData.parishioner_discounts.map((discount, idx) => (
                    <div key={idx} className="p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-sm text-[#2D2A26] font-['Nunito']">{discount.title}</h4>
                          <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">{discount.description}</p>
                          {discount.discount_value && (
                            <Badge className="mt-2 bg-purple-100 text-purple-700 text-[10px]">
                              {discount.discount_value}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDiscount(idx)}
                          className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Discount */}
              <div className="p-4 border-2 border-dashed border-[#E5E0D5] rounded-lg space-y-3">
                <h4 className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">Add a Discount</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-['Nunito'] text-xs">Discount Title *</Label>
                    <Input 
                      value={newDiscount.title}
                      onChange={e => setNewDiscount({...newDiscount, title: e.target.value})}
                      placeholder="e.g., Parish Member Special"
                      className="border-[#E5E0D5] font-['Nunito'] text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-['Nunito'] text-xs">Discount Value</Label>
                    <Input 
                      value={newDiscount.discount_value}
                      onChange={e => setNewDiscount({...newDiscount, discount_value: e.target.value})}
                      placeholder="e.g., 10% off, $20 off"
                      className="border-[#E5E0D5] font-['Nunito'] text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs">Description *</Label>
                  <Textarea 
                    value={newDiscount.description}
                    onChange={e => setNewDiscount({...newDiscount, description: e.target.value})}
                    placeholder="Describe the discount and how to redeem it..."
                    className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[60px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-['Nunito'] text-xs">Conditions (optional)</Label>
                  <Input 
                    value={newDiscount.conditions}
                    onChange={e => setNewDiscount({...newDiscount, conditions: e.target.value})}
                    placeholder="e.g., Valid on first visit only"
                    className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  />
                </div>
                <Button 
                  type="button"
                  onClick={addDiscount}
                  variant="outline"
                  className="w-full border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] font-['Nunito']"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Discount
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline"
              onClick={() => navigate(`/directory/${listingId}`)}
              className="flex-1 border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="save-business-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
